import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, ShieldCheck, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth, useCart } from "../context/AppContext";
import { signOut } from "../lib/dataStore";

export default function Header({ onOpenCart }) {
  const { user, isAdmin } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-terracotta" : "text-foreground/80 hover:text-terracotta"}`;

  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2">
          <span className="font-serif text-2xl tracking-tight text-terracotta">Lineacraft</span>
          <span className="overline text-muted-foreground">India</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop" className={linkCls} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/orders" className={linkCls} data-testid="nav-orders">My Orders</NavLink>
          {isAdmin && <NavLink to="/admin" className={linkCls} data-testid="nav-admin">Admin</NavLink>}
          <Link
            to="/shop"
            data-testid="header-shop-now-btn"
            className="px-4 py-1.5 rounded-full bg-terracotta text-white text-xs font-semibold shadow-md hover:bg-terracotta/90 hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 glow-button"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Shop Now</span>
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={onOpenCart}
            data-testid="header-cart-btn"
            className="relative p-2 hover:text-terracotta transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-terracotta text-primary-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {isAdmin && <ShieldCheck data-testid="admin-badge" className="w-4 h-4 text-moss" />}
              <span className="text-sm text-muted-foreground max-w-[140px] truncate">{user.displayName || user.email}</span>
              <button data-testid="signout-btn" onClick={() => { signOut(); nav("/"); }} className="p-2 hover:text-terracotta" aria-label="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-login-btn" className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-foreground/20 hover:bg-foreground hover:text-background transition-colors">
              <User className="w-4 h-4" /> Sign In
            </Link>
          )}
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="menu" data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border/60 px-6 py-4 flex flex-col gap-4 bg-background">
          <NavLink onClick={() => setOpen(false)} to="/" end className={linkCls}>Home</NavLink>
          <NavLink onClick={() => setOpen(false)} to="/shop" className={linkCls}>Shop</NavLink>
          <NavLink onClick={() => setOpen(false)} to="/orders" className={linkCls}>My Orders</NavLink>
          {isAdmin && <NavLink onClick={() => setOpen(false)} to="/admin" className={linkCls}>Admin</NavLink>}
          {user ? (
            <button onClick={() => { signOut(); setOpen(false); nav("/"); }} className="text-left text-sm">Sign out</button>
          ) : (
            <Link onClick={() => setOpen(false)} to="/login" className="text-sm">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}
